import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_SETTINGS_BY_TYPE } from "../graphql/queries";
import { AppSettingsValue, GetSettingsByTypeResponse, Setting } from "../types";
import { getAppConfig } from "../utils/appSettings";

export interface AppSettingsContextValue {
  /** Настройки приложения (type app_{schema}) */
  appConfig: AppSettingsValue | null;
  /** Первая запись app_* */
  setting: Setting | null;
  /** Массив настроек из getSettingsByType */
  settings: Setting[];
  /** Найти настройку по type */
  getByType: (type: string) => Setting | undefined;
  /** Ответ API — массив Setting[] (пустой, если записей нет) */
  raw: Setting[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

const AppSettingsContext = createContext<AppSettingsContextValue | null>(null);

export function AppSettingsProvider({ children }: { children: React.ReactNode }) {
  const { data, loading, error, refetch } = useQuery<GetSettingsByTypeResponse>(
    GET_SETTINGS_BY_TYPE
  );

  const raw = useMemo(() => data?.getSettingsByType ?? [], [data?.getSettingsByType]);
  const settings = raw;
  const appConfig = useMemo(() => getAppConfig(settings), [settings]);
  const setting = useMemo(
    () => settings.find((s) => s.type.startsWith("app_")) ?? null,
    [settings]
  );

  const getByType = useCallback(
    (type: string) => settings.find((s) => s.type === type),
    [settings]
  );

  const refetchCb = useCallback(() => {
    void refetch();
  }, [refetch]);

  const value = useMemo<AppSettingsContextValue>(
    () => ({
      appConfig,
      setting,
      settings,
      getByType,
      raw,
      loading,
      error: error as Error | undefined,
      refetch: refetchCb,
    }),
    [appConfig, setting, settings, getByType, raw, loading, error, refetchCb]
  );

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettings(): AppSettingsContextValue {
  const ctx = useContext(AppSettingsContext);
  if (ctx == null) {
    throw new Error("useAppSettings must be used within AppSettingsProvider");
  }
  return ctx;
}
